import { createSelector } from 'reselect';
import { ff } from '@atlassian/jira-feature-flagging';
import type { DateFormat } from '../../common/model';
import type { ReleasableVersion, OnOpened, OnError, OnSuccess, OnCancel } from '../model';
import type { State } from './types';

export const getBaseUrl = (state: State): string => state.context.baseUrl;

export const getProjectKey = (state: State): string => state.context.projectKey;

export const getVersion = (state: State): ReleasableVersion => state.version;

export const getSwapVersion = (state: State): string | undefined =>
	state.version.moveUnfixedIssuesTo;

export const getVersionId = (state: State): string => state.version.id;

export const getTodoInProgressIssuesCount = (state: State): number => {
	if (state.version.issuesStatus) {
		return state.version.issuesStatus.toDo + state.version.issuesStatus.inProgress;
	}
	return 0;
};

export const getUnresolvedIssuesCount = (state: State): number => state.unresolvedIssuesCount;

export const getTotalIssuesCount = (state: State): number => {
	if (state.version.issuesStatus) {
		return (
			state.version.issuesStatus.done +
			state.version.issuesStatus.toDo +
			state.version.issuesStatus.inProgress
		);
	}
	return 0;
};

export const getResolvedIssuesCount = (state: State): number =>
	getTotalIssuesCount(state) - getUnresolvedIssuesCount(state);

export const getDoneIssuesCount = (state: State): number => {
	if (state.version.issuesStatus) {
		return state.version.issuesStatus.done;
	}
	return 0;
};

export const getName = (state: State): string => getVersion(state).name || '';

export const getReleaseDate = (state: State): DateFormat => getVersion(state).releaseDate || '';

export const getErrors = createSelector(
	// @ts-expect-error - TS2571 - Object is of type 'unknown'.
	(state) => state.errors,
	(errors) => {
		const { releaseDate = '', moveUnfixedIssuesTo = '' } = errors || {};
		return {
			releaseDate,
			moveUnfixedIssuesTo,
		};
	},
);

export const isSwapSelected = (state: State): boolean => state.shouldSwapIssues;

export const isSaving = (state: State): boolean => !!state.isSaving;

export const isInitiating = (state: State): boolean => !!state.isInitiating;

export const getOnOpenedHandler = (state: State): OnOpened => state.handlers.onOpened;

export const getOnErrorHandler = (state: State): OnError => state.handlers.onError;

export const getOnSuccessHandler = (state: State): OnSuccess => state.handlers.onSuccess;

export const getOnCancelHandler = (state: State): OnCancel => state.handlers.onCancel;

export const getPropsForVersionPicker = (state: State) => ({
	baseUrl: state.context.baseUrl,
	projectKey: state.context.projectKey,
	shouldSwapIssues: state.shouldSwapIssues,
	isSaving: state.isSaving,
	versionId: state.version.id,
});

const hasTodoInProgressIssues = (state: State): boolean => getTodoInProgressIssuesCount(state) > 0;

export const isReleaseDisabled = (state: State): boolean => {
	if (!ff('show-correct-unresolved-issues-totals_dmden')) {
		return (
			state.isSaving ||
			(hasTodoInProgressIssues(state) && state.shouldSwapIssues && !getSwapVersion(state))
		);
	}

	return (
		state.isSaving ||
		(getUnresolvedIssuesCount(state) > 0 && state.shouldSwapIssues && !getSwapVersion(state))
	);
};

export const isKeepingIssues = (state: State): boolean => {
	if (hasTodoInProgressIssues(state)) {
		return state.shouldSwapIssues ? !getSwapVersion(state) : true;
	}
	return true;
};

export const shouldShowCreateReleaseNotesOption = (state: State): boolean =>
	state.shouldShowCreateReleaseNotesOption;

export const shouldRedirectToCreateReleaseNotes = (state: State): boolean =>
	state.shouldRedirectToCreateReleaseNotes;
